import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/design-system/Button'

import { useAdminMutation, useAdminQuery } from '../api'
import { AdminHeader } from '../AdminLayout'
import { CollectionEditor, FormActions } from '../components/CollectionEditor'
import { SwitchField, TextField } from '../components/fields'
import { RbacError } from '../components/RbacError'
import { useAdminContext, type AdminEditionSummary } from '../session'
import { useCollection } from '../useCollection'
import type { I18n, Role, StaffAccount } from '../types'

/**
 * Les comptes de l'équipe et leurs rôles.
 *
 * Un piège mérite d'être connu, parce qu'il coûte une soirée : **un rôle
 * attribué sur une édition n'accorde rien sans l'en-tête `X-Event-Id`**. Les
 * surfaces `/ops` l'envoient — c'est leur métier —, mais un rôle scopé reste
 * un rôle limité, et la personne ne verra pas les autres éditions. Un rôle
 * global (« toutes éditions ») est l'inverse : il suit la personne partout.
 *
 * L'écran affiche donc la portée de chaque rôle en toutes lettres, plutôt
 * qu'un code que personne ne relit.
 */
export function AdminTeamPage() {
  const { t, i18n } = useTranslation()
  const { eventId, editions } = useAdminContext()
  // Le mot de passe provisoire n'existe que dans une réponse. On le garde à
  // l'écran jusqu'à ce que la personne le referme : rechargé, il est perdu, et
  // aucune route ne le redonnera.
  const [secret, setSecret] = useState<{ email: string; password: string } | null>(null)

  useEffect(() => {
    document.title = t('admin.section.team')
  }, [t])

  const users = useCollection<StaffAccount>({
    key: 'admin-users',
    path: '/admin/users',
    eventId,
  })
  // Les noms des rôles viennent de `/admin/users/role-options`, pas de
  // `/admin/roles` : ce dernier exige le droit de COMPOSER les rôles, et qui
  // ne fait qu'inscrire un bénévole voyait alors des codes bruts et une liste
  // d'attribution vide.
  const rolesQuery = useAdminQuery<Role[]>(
    ['admin-role-options', eventId],
    '/admin/users/role-options',
    eventId,
  )
  const roles = rolesQuery.data ?? []

  /** Le nom d'un rôle dans la langue de l'écran ; le code en dernier recours. */
  function roleName(code: string, carried?: I18n): string {
    const fromPayload = carried ? localized(carried, i18n.language) : ''
    if (fromPayload) return fromPayload
    const role = roles.find((entry) => entry.code === code)
    return (role && localized(role.name_i18n, i18n.language)) || code
  }

  return (
    <>
      <AdminHeader title={t('admin.section.team')} description={t('admin.team.description')} />

      <CollectionEditor<StaffAccount>
        title={t('admin.team.accounts')}
        description={t('admin.team.accountsHint')}
        items={users.items}
        busy={users.busy}
        error={users.error}
        emptyLabel={t('admin.team.noAccounts')}
        createLabel={t('admin.team.addAccount')}
        summarize={(account) => ({
          title: account.full_name,
          subtitle: account.email,
          value:
            account.roles.length === 0
              ? t('admin.team.noRole')
              : account.roles
                  .map((role) => roleName(role.role_code, role.role_name_i18n))
                  .join(' · '),
          dimmed: !account.is_active,
          badges: [
            ...(account.is_active
              ? []
              : [{ label: t('admin.team.disabled'), tone: 'warning' } as const]),
            // Le mot de passe provisoire se voit dans la liste : c'est la
            // question qu'on se pose en premier (« a-t-il pu se connecter ? »).
            ...(account.must_change_password
              ? [{ label: t('admin.team.provisionalBadge'), tone: 'neutral' } as const]
              : []),
          ],
        })}
        renderForm={(account, close) => (
          <AccountPanel
            account={account}
            roles={roles}
            roleName={roleName}
            eventId={eventId}
            editions={editions}
            busy={users.busy}
            onCancel={close}
            onSecret={setSecret}
            onSubmit={(payload) => {
              if (account) {
                users.update.mutate({ id: account.id, ...payload }, { onSuccess: close })
                return
              }
              users.create.mutate(payload, {
                onSuccess: (created) => {
                  // La réponse de création porte le mot de passe tiré au sort.
                  const fresh = created as StaffAccount & { temporary_password?: string }
                  if (fresh.temporary_password) {
                    setSecret({ email: fresh.email, password: fresh.temporary_password })
                  }
                  close()
                },
              })
            }}
          />
        )}
      />

      {secret ? (
        <SecretDialog secret={secret} onClose={() => setSecret(null)} />
      ) : null}
    </>
  )
}

/**
 * Le mot de passe provisoire, montré une fois.
 *
 * Il n'est écrit nulle part — ni en base, ni dans le journal d'audit — et
 * aucune route ne le redonnera. Le panneau le dit franchement plutôt que de
 * laisser croire qu'on le retrouvera : c'est ce qui décide la personne à le
 * transmettre tout de suite.
 */
function SecretDialog({
  secret,
  onClose,
}: {
  secret: { email: string; password: string }
  onClose: () => void
}) {
  const { t } = useTranslation()
  const [copied, setCopied] = useState(false)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" role="dialog" aria-modal>
      <div className="w-full max-w-md rounded-lg border border-champagne-400/40 bg-bg-base p-6">
        <h2 className="font-display text-heading-md text-content-primary">
          {t('admin.team.secretTitle')}
        </h2>
        {/* « Mon compte » existe désormais : le texte y renvoie, et dit que le
            changement sera exigé à la première connexion. */}
        <p className="mt-2 text-sm text-content-secondary">{t('admin.team.secretBodyAccount')}</p>

        <p className="mt-4 text-caption text-content-muted">{secret.email}</p>
        <p className="mt-1 select-all break-all rounded-md border border-line bg-bg-elevated px-3 py-3 font-mono text-sm text-champagne-300">
          {secret.password}
        </p>

        <div className="mt-5 flex flex-wrap gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              void navigator.clipboard?.writeText(secret.password)
              setCopied(true)
            }}
          >
            {copied ? t('admin.team.copied') : t('admin.team.copy')}
          </Button>
          <Button size="sm" onClick={onClose}>
            {t('admin.team.secretDone')}
          </Button>
        </div>
      </div>
    </div>
  )
}

function AccountPanel({
  account,
  roles,
  roleName,
  eventId,
  editions,
  busy,
  onSubmit,
  onCancel,
  onSecret,
}: {
  account: StaffAccount | null
  roles: Role[]
  roleName: (code: string, carried?: I18n) => string
  eventId: string | undefined
  editions: AdminEditionSummary[]
  busy: boolean
  onSubmit: (payload: Record<string, unknown>) => void
  onCancel: () => void
  onSecret: (secret: { email: string; password: string }) => void
}) {
  const { t, i18n } = useTranslation()
  const [email, setEmail] = useState(account?.email ?? '')
  const [fullName, setFullName] = useState(account?.full_name ?? '')
  const [phone, setPhone] = useState(account?.phone ?? '')
  const [active, setActive] = useState(account?.is_active ?? true)
  const [roleCode, setRoleCode] = useState('')
  // La portée de l'attribution : '' = toutes les éditions, sinon l'identifiant
  // d'une édition. Par défaut l'édition en cours — le cas le plus fréquent, et
  // le moins risqué : un rôle global suit la personne partout.
  const [scope, setScope] = useState<string>(eventId ?? '')
  const [confirmingDelete, setConfirmingDelete] = useState(false)

  /** Le libellé d'une édition : son nom, et l'année quand le nom ne la dit pas. */
  function editionLabel(name: I18n | null | undefined, year: number | null | undefined): string {
    const label = name ? localized(name, i18n.language) : ''
    if (!label) return year ? String(year) : t('admin.team.unknownEdition')
    return year && !label.includes(String(year)) ? `${label} ${year}` : label
  }

  const isNew = account === null
  const invalidate = [['admin-users', eventId]]

  const assign = useAdminMutation<
    StaffAccount,
    { role_code: string; event_id: string | null }
  >(
    'post',
    () => `/admin/users/${account?.id}/roles`,
    eventId,
    invalidate,
  )
  const revoke = useAdminMutation<void, { userRoleId: string }>(
    'delete',
    (variables) => `/admin/users/${account?.id}/roles/${variables.userRoleId}`,
    eventId,
    invalidate,
  )
  const reset = useAdminMutation<
    { email: string; temporary_password: string },
    Record<string, never>
  >('post', () => `/admin/users/${account?.id}/reset-password`, eventId, invalidate)
  const remove = useAdminMutation<{ message: string }, Record<string, never>>(
    'delete', () => `/admin/users/${account?.id}`, eventId, invalidate,
  )

  const working =
    busy || assign.isPending || revoke.isPending || reset.isPending || remove.isPending
  const error = assign.error ?? revoke.error ?? reset.error ?? remove.error

  return (
    <div>
      <div className="grid gap-5 sm:grid-cols-2">
        {/* L'adresse est l'identifiant de connexion : une faute de frappe à la
            création enfermerait le compte dehors. Elle se corrige donc, et le
            serveur coupe les sessions ouvertes quand elle change. */}
        <TextField
          label={t('admin.email')}
          type="email"
          {...(isNew ? {} : { hint: t('admin.team.emailHint') })}
          value={email}
          onChange={setEmail}
        />
        <TextField label={t('admin.team.fullName')} value={fullName} onChange={setFullName} />
        <TextField label={t('admin.event.contactPhone')} type="tel" value={phone} onChange={setPhone} />
        {isNew ? null : (
          <SwitchField
            label={t('admin.team.accountActive')}
            hint={t('admin.team.accountActiveHint')}
            value={active}
            onChange={setActive}
          />
        )}
      </div>

      {isNew ? (
        <p className="mt-4 text-caption text-content-muted">
          {t('admin.team.passwordGenerated')}
        </p>
      ) : null}

      {/* Le badge de la liste dit « provisoire » ; ici on explique ce que ça
          implique, pour qu'on ne renvoie pas un second mot de passe à
          quelqu'un qui n'a simplement pas encore ouvert le back-office. */}
      {account?.must_change_password ? (
        <p className="mt-4 rounded-md border border-line-subtle bg-bg-elevated px-3 py-2 text-sm text-content-secondary">
          {t('admin.team.provisionalHint')}
        </p>
      ) : null}

      <FormActions
        busy={working || !fullName.trim() || !email.trim()}
        onCancel={onCancel}
        onSubmit={() =>
          onSubmit(
            isNew
              ? {
                  email: email.trim(),
                  full_name: fullName.trim(),
                  phone: phone.trim() || null,
                  is_staff: true,
                }
              : {
                  email: email.trim(),
                  full_name: fullName.trim(),
                  phone: phone.trim() || null,
                  is_active: active,
                },
          )
        }
      />

      {/* Réinitialiser et supprimer : deux gestes qui ne se corrigent pas, donc
          rangés à part, après le formulaire. */}
      {account ? (
        <section className="mt-8 border-t border-line-subtle pt-6">
          <p className="text-caption uppercase tracking-wide text-content-muted">
            {t('admin.team.dangerZone')}
          </p>

          <div className="mt-3 flex flex-wrap items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              disabled={working}
              onClick={() =>
                reset.mutate(
                  {},
                  {
                    onSuccess: (result) =>
                      onSecret({
                        email: result.email,
                        password: result.temporary_password,
                      }),
                  },
                )
              }
            >
              {t('admin.team.resetPassword')}
            </Button>

            {confirmingDelete ? (
              <>
                <span className="text-sm text-content-secondary">
                  {t('admin.team.deleteConfirm')}
                </span>
                <Button
                  size="sm"
                  disabled={working}
                  onClick={() => remove.mutate({}, { onSuccess: onCancel })}
                >
                  {t('admin.team.deleteYes')}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setConfirmingDelete(false)}
                >
                  {t('admin.project.cancel')}
                </Button>
              </>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                disabled={working}
                onClick={() => setConfirmingDelete(true)}
              >
                {t('admin.team.deleteAccount')}
              </Button>
            )}
          </div>

          <p className="mt-2 text-caption text-content-muted">
            {t('admin.team.deleteHint')}
          </p>
        </section>
      ) : null}

      {/* Les rôles ne se gèrent que sur un compte existant. */}
      {account ? (
        <section className="mt-8 border-t border-line-subtle pt-6">
          <p className="text-caption uppercase tracking-wide text-content-muted">
            {t('admin.team.roles')}
          </p>

          {account.roles.length === 0 ? (
            <p className="mt-2 text-sm text-content-muted">{t('admin.team.noRole')}</p>
          ) : (
            <ul className="mt-3 space-y-1.5">
              {account.roles.map((role) => (
                <li
                  key={role.id}
                  className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 rounded-md border border-line-subtle px-3 py-2"
                >
                  <span className="min-w-0 flex-1 text-sm">
                    <span className="text-content-primary">
                      {roleName(role.role_code, role.role_name_i18n)}
                    </span>
                    {/* La portée passe à la ligne plutôt que de pousser la
                        croix de retrait hors de l'écran. Elle nomme l'édition
                        RÉELLE de l'attribution, lue dans la réponse : afficher
                        l'édition sélectionnée faisait croire qu'un rôle 2025
                        valait pour 2026. */}
                    <span className="ml-2 inline-block text-xs text-content-muted">
                      {role.event_id
                        ? t('admin.team.scopedTo', {
                            event: editionLabel(role.event_name_i18n, role.event_year),
                          })
                        : t('admin.team.allEditions')}
                    </span>
                  </span>
                  <button
                    type="button"
                    disabled={working}
                    onClick={() => revoke.mutate({ userRoleId: role.id })}
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md text-content-muted transition hover:text-state-danger"
                    aria-label={t('admin.team.revoke')}
                  >
                    ✕
                  </button>
                </li>
              ))}
            </ul>
          )}

          <div className="mt-4 grid gap-3 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
            <label className="block min-w-0">
              <span className="text-caption uppercase tracking-wide text-content-muted">
                {t('admin.team.addRole')}
              </span>
              <select
                value={roleCode}
                onChange={(changeEvent) => setRoleCode(changeEvent.target.value)}
                className="mt-1.5 w-full rounded-md border border-line bg-bg-base px-3 py-2.5 text-content-primary"
              >
                <option value="">{t('admin.team.pickRole')}</option>
                {roles.map((role) => (
                  <option key={role.id} value={role.code}>
                    {roleName(role.code)}
                  </option>
                ))}
              </select>
            </label>
            <label className="block min-w-0">
              <span className="text-caption uppercase tracking-wide text-content-muted">
                {t('admin.team.scope')}
              </span>
              <select
                value={scope}
                onChange={(changeEvent) => setScope(changeEvent.target.value)}
                className="mt-1.5 w-full rounded-md border border-line bg-bg-base px-3 py-2.5 text-content-primary"
              >
                <option value="">{t('admin.team.allEditions')}</option>
                {/* L'édition courante peut manquer à la liste (compte limité,
                    liste pas encore chargée) : on la garde choisissable plutôt
                    que de basculer en silence sur « toutes les éditions ». */}
                {scope && !editions.some((edition) => edition.id === scope) ? (
                  <option value={scope}>{t('admin.team.currentEdition')}</option>
                ) : null}
                {editions.map((edition) => (
                  <option key={edition.id} value={edition.id}>
                    {editionLabel(edition.name_i18n, edition.edition_year)}
                  </option>
                ))}
              </select>
            </label>
            <Button
              size="sm"
              variant="outline"
              disabled={working || !roleCode}
              onClick={() =>
                assign.mutate(
                  { role_code: roleCode, event_id: scope || null },
                  { onSuccess: () => setRoleCode('') },
                )
              }
            >
              {t('admin.team.grant')}
            </Button>
          </div>
          <p className="mt-2 text-xs text-content-muted">{t('admin.team.scopeHint')}</p>
        </section>
      ) : null}

      {/* Un refus de délégation énumère les permissions manquantes : on les
          montre, sinon on cherche au hasard pourquoi le rôle « ne passe pas ». */}
      <RbacError error={error} />
    </div>
  )
}

/** Un texte trilingue dans la langue de l'écran, ou la première langue remplie. */
function localized(value: I18n, language: string): string {
  return value[language] || Object.values(value).find((entry) => entry?.trim()) || ''
}
